import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Camera, Trash2, Star, StarOff, GripVertical } from 'lucide-react';
import { GALLERY_CATEGORIES, CATEGORY_META, buildImgUrl } from '@/components/Gallery';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PER_CATEGORY = 10;

function SortableThumb({ img, index, activeCat, t, onSetCover, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.image_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square rounded-xl overflow-hidden border border-border group touch-none"
      data-testid={`mgr-item-${index}`}
    >
      <img src={buildImgUrl(img.url_thumb)} alt="" loading="lazy" className="w-full h-full object-cover pointer-events-none" />
      {/* Drag handle (top-right) */}
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/90 hover:bg-white text-foreground/70 shadow-sm cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        title={t('gal_drag_hint')}
        data-testid={`drag-handle-${index}`}
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </button>
      {img.is_cover && (
        <div className="absolute top-1.5 left-1.5 bg-primary text-white text-[10px] font-sans px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`cover-badge-${index}`}>
          <Star className="w-2.5 h-2.5 fill-white" strokeWidth={2} /> Cover
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 py-2">
        {activeCat === 'LOCATION' && !img.is_cover && (
          <button onClick={() => onSetCover(img.image_id)} className="p-2 rounded-full bg-white/90 hover:bg-white" title={t('gal_set_cover')} data-testid={`set-cover-${index}`}>
            <StarOff className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </button>
        )}
        <button onClick={() => onDelete(img.image_id)} className="p-2 rounded-full bg-white/90 hover:bg-white" title={t('gal_delete')} data-testid={`delete-${index}`}>
          <Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

export default function GalleryManager({ gallery, t, onChange }) {
  const [filter, setFilter] = useState('LOCATION');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const activeCat = filter === 'ALL' ? 'LOCATION' : filter;

  // Local ordered list for optimistic drag reordering
  const serverCatItems = useMemo(
    () => gallery.filter(g => g.category === activeCat).sort((a, b) => a.order - b.order),
    [gallery, activeCat]
  );
  const [localItems, setLocalItems] = useState(serverCatItems);
  useEffect(() => { setLocalItems(serverCatItems); }, [serverCatItems]);

  const catCount = localItems.length;
  const atCap = catCount >= MAX_PER_CATEGORY;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('gal_too_big')); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', activeCat);
    try {
      await axios.post(`${API}/business/gallery/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('gal_uploaded'));
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gal_upload_err'));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async (image_id) => {
    if (!window.confirm(t('gal_confirm_delete'))) return;
    try {
      await axios.delete(`${API}/business/gallery/${image_id}`, { withCredentials: true });
      toast.success(t('gal_deleted'));
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSetCover = async (image_id) => {
    try {
      await axios.put(`${API}/business/gallery/${image_id}/cover`, {}, { withCredentials: true });
      toast.success(t('gal_cover_set'));
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localItems.findIndex(i => i.image_id === active.id);
    const newIndex = localItems.findIndex(i => i.image_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(reordered); // optimistic
    try {
      await axios.put(
        `${API}/business/gallery/reorder`,
        { category: activeCat, image_ids: reordered.map(i => i.image_id) },
        { withCredentials: true }
      );
      toast.success(t('gal_reorder_saved'));
      onChange();
    } catch (err) {
      setLocalItems(serverCatItems); // revert
      toast.error(err.response?.data?.detail || t('gal_reorder_err'));
    }
  };

  return (
    <div className="space-y-4" data-testid="gallery-manager">
      {/* Category tabs (each acts as filter + target upload category) */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {GALLERY_CATEGORIES.map(c => {
          const Icon = CATEGORY_META[c].icon;
          const count = gallery.filter(g => g.category === c).length;
          const isActive = activeCat === c;
          return (
            <button key={c} onClick={() => setFilter(c)} data-testid={`mgr-tab-${c.toLowerCase()}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-sans font-medium transition-colors border ${isActive ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {t(CATEGORY_META[c].labelKey)}
              <span className={`text-[10px] ${isActive ? 'opacity-90' : 'opacity-60'}`}>({count}/{MAX_PER_CATEGORY})</span>
            </button>
          );
        })}
      </div>

      {/* Upload */}
      <div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" data-testid="gallery-file-input" />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => fileRef.current?.click()} disabled={uploading || atCap} className="rounded-full btn-lift" data-testid="gallery-upload-btn">
            <Camera className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {uploading ? t('gal_uploading') : atCap ? t('gal_cat_full') : t('gal_add_photo')}
          </Button>
          {localItems.length > 1 && (
            <p className="text-xs text-muted-foreground font-sans" data-testid="gallery-drag-hint">{t('gal_drag_hint')}</p>
          )}
        </div>
        {atCap && <p className="text-xs text-amber-600 font-sans mt-1.5" data-testid="gallery-cap-warn">{t('gal_max_reached')}</p>}
      </div>

      {/* Grid with DnD */}
      {localItems.length === 0 ? (
        <div className="w-full py-10 border border-dashed border-border rounded-xl text-center text-muted-foreground text-sm font-sans">{t('gal_no_photos_cat')}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localItems.map(i => i.image_id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-sortable-grid">
              {localItems.map((img, i) => (
                <SortableThumb
                  key={img.image_id}
                  img={img}
                  index={i}
                  activeCat={activeCat}
                  t={t}
                  onSetCover={handleSetCover}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
